/**
 * VerificationCard — Global Trust Layer Component
 *
 * Reusable across: AssessmentWorkspace, SME Dashboard, FI Review, Deal Pipeline.
 * Shows verification level, confidence, evidence coverage, and upgrade path.
 *
 * Usage:
 *   <VerificationCard assessmentId={id} />
 *   <VerificationCard assessmentId={id} compact={true} />
 */
import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import {
  Shield, Check, AlertTriangle, Info, AlertCircle, RefreshCw,
  ChevronRight, X, ArrowRight, ExternalLink, CheckCircle2,
  FileText, Upload, Zap, Lock
} from "lucide-react";

// ── Level configuration ───────────────────────────────────────────────────
const LEVELS = {
  self_reported:        { idx: 0, label: "Self-Reported",       short: "Self",    color: "#94A3B8", bg: "bg-slate-100",   text: "text-slate-600",   border: "border-slate-300" },
  system_validated:     { idx: 1, label: "System Validated",    short: "System",  color: "#3B82F6", bg: "bg-blue-50",     text: "text-blue-700",    border: "border-blue-300"  },
  analyst_reviewed:     { idx: 2, label: "Analyst Reviewed",    short: "Analyst", color: "#6366F1", bg: "bg-indigo-50",   text: "text-indigo-700",  border: "border-indigo-300"},
  third_party_verified: { idx: 3, label: "Third-Party Verified",short: "3P",      color: "#059669", bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-300"},
};
const RISK_STYLE = {
  high:   "bg-red-50 border-red-200 text-red-700",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  low:    "bg-slate-50 border-slate-200 text-slate-600",
};
const fmt = (n, d = 0) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";

// ── Verification Progress Dots ────────────────────────────────────────────
function LevelDots({ currentIdx }) {
  return (
    <div className="flex items-center gap-1.5">
      {[0, 1, 2, 3].map(i => {
        const lvl = Object.values(LEVELS)[i];
        return (
          <div key={i} className={`rounded-full transition-all ${
            i < currentIdx ? "w-4 h-3" : i === currentIdx ? "w-5 h-3" : "w-3 h-3"
          }`}
            style={{ backgroundColor: i <= currentIdx ? lvl.color : "#E2E8F0" }} />
        );
      })}
    </div>
  );
}

// ── Details Modal ─────────────────────────────────────────────────────────
function DetailsModal({ data, onClose, onUpgradeClick }) {
  const lvl = LEVELS[data.verification_level] || LEVELS.self_reported;

  const confBars = [
    { label: "Completeness",      key: "completeness",       weight: "25%" },
    { label: "Evidence Coverage", key: "evidence_score",     weight: "25%" },
    { label: "Consistency",       key: "consistency",        weight: "20%" },
    { label: "Source Reliability",key: "source_reliability", weight: "15%" },
    { label: "Benchmark Fit",     key: "benchmark_fit",      weight: "15%" },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="verif-details-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#0F172A] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Verification Details</p>
            <p className="text-white font-bold">{data.assessment_title}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${lvl.text} ${lvl.bg} ${lvl.border}`}>
              {lvl.label}
            </span>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* A: Confidence Breakdown */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Confidence Breakdown</h3>
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-shrink-0" style={{width:72,height:72}}>
                <svg width="72" height="72" viewBox="0 0 72 72">
                  <circle cx="36" cy="36" r="27" fill="none" stroke="#E2E8F0" strokeWidth="7" />
                  <circle cx="36" cy="36" r="27" fill="none" stroke={lvl.color} strokeWidth="7"
                    strokeDasharray={`${((data.confidence_score||0)/100)*169.6} 169.6`}
                    strokeDashoffset="42.4" strokeLinecap="round" />
                  <text x="36" y="32" textAnchor="middle" fill="#0F172A" fontSize="13" fontWeight="700">{fmt(data.confidence_score)}</text>
                  <text x="36" y="45" textAnchor="middle" fill="#94A3B8" fontSize="8">/100</text>
                </svg>
              </div>
              <div className="flex-1 space-y-2">
                {confBars.map(({ label, key, weight }) => {
                  const val = data[key] || 0;
                  const bc  = val >= 75 ? "#059669" : val >= 50 ? "#3B82F6" : val >= 40 ? "#D97706" : "#DC2626";
                  return (
                    <div key={key}>
                      <div className="flex justify-between mb-0.5">
                        <span className="text-xs text-slate-500">{label} <span className="text-slate-300">{weight}</span></span>
                        <span className="text-xs font-bold tabular-nums" style={{color:bc}}>{fmt(val)}%</span>
                      </div>
                      <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{width:`${val}%`,backgroundColor:bc}} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* B: Evidence Coverage */}
          <section className="border-t border-slate-100 pt-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Evidence Coverage</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                ["Total Inputs",     data.activity_count || 0],
                ["Evidence Files",   data.evidence_total || 0],
                ["Verified",         data.evidence_verified || 0],
              ].map(([label, val]) => (
                <div key={label} className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-[#0F172A] tabular-nums">{val}</p>
                  <p className="text-xs text-slate-400">{label}</p>
                </div>
              ))}
            </div>
            {(data.activity_count || 0) > 0 && (
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full"
                  style={{width:`${data.evidence_coverage_pct || 0}%`}} />
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1">{fmt(data.evidence_coverage_pct)}% coverage</p>
          </section>

          {/* C: Validation Results */}
          {data.validation_ran_at && (
            <section className="border-t border-slate-100 pt-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Validation Results</h3>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-2 ${data.is_valid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                {data.is_valid ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                {data.is_valid ? `All checks passed — ${data.warning_count} warning(s)` : `${data.issue_count} issue(s), ${data.warning_count} warning(s)`}
              </div>
              {[...(data.validation_issues||[]).slice(0,3), ...(data.validation_warnings||[]).slice(0,3)].map((item, i) => (
                <div key={i} className={`text-xs px-3 py-1.5 rounded mb-1 ${item.severity==="error"?"bg-red-50 text-red-700":"bg-amber-50 text-amber-700"}`}>
                  {item.message}
                </div>
              ))}
            </section>
          )}

          {/* D: Key Risks */}
          {(data.key_risks||[]).length > 0 && (
            <section className="border-t border-slate-100 pt-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Key Risks</h3>
              <div className="space-y-2">
                {data.key_risks.map((r, i) => (
                  <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${RISK_STYLE[r.level]||RISK_STYLE.low}`}>
                    {r.level==="high" ? <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> : <Info size={12} className="mt-0.5 flex-shrink-0" />}
                    <span>{r.text}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* E: History */}
          {(data.review_requests||[]).length > 0 && (
            <section className="border-t border-slate-100 pt-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Review History</h3>
              {data.review_requests.map((r, i) => (
                <div key={i} className="flex items-center gap-3 text-xs py-2 border-b border-slate-50">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.status==="approved"?"bg-emerald-500":r.status==="pending"?"bg-amber-500":"bg-red-400"}`} />
                  <span className="text-slate-700 capitalize font-medium">{r.status}</span>
                  <span className="text-slate-400">{r.submitted_at?.slice(0,10)}</span>
                  {r.notes && <span className="text-slate-400 italic truncate">"{r.notes}"</span>}
                </div>
              ))}
            </section>
          )}

          {/* CTA */}
          {data.verification_level !== "third_party_verified" && (
            <div className="border-t border-slate-100 pt-4 flex gap-3">
              <button onClick={() => { onClose(); onUpgradeClick(); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700">
                <ArrowRight size={14} /> Upgrade Verification
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Upgrade Flow ──────────────────────────────────────────────────────────
function UpgradeFlow({ data, assessmentId, onClose, onSuccess }) {
  const [step,       setStep]       = useState(0);
  const [recomputing,setRecomputing]= useState(false);
  const [result,     setResult]     = useState(null);

  const requirements = [
    data.issue_count > 0   && { id:"fix_issues",   icon: AlertTriangle, label: "Fix validation issues",   desc: `${data.issue_count} error(s) must be resolved`, done: false },
    (data.completeness||0) < 70 && { id:"completeness",  icon: FileText,     label: "Add missing activity records", desc: `Completeness at ${fmt(data.completeness)}% — target 70%+`, done: false },
    (data.evidence_score||0) < 50 && { id:"evidence",      icon: Upload,      label: "Upload supporting documents", desc: `Evidence coverage at ${fmt(data.evidence_score)}% — target 50%+`, done: false },
    (!data.validation_ran_at) && { id:"not_validated",icon: Shield,       label: "Run input validation first",  desc: "Validation has not been run yet", done: false },
  ].filter(Boolean);

  const allMet = requirements.length === 0 || requirements.every(r => r.done);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await client.post(`/confidence/assessments/${assessmentId}/recompute`);
      setResult(res.data);
      if (res.data.upgraded) {
        toast.success("Verification upgraded to System Validated!");
        onSuccess?.();
      }
      setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || "Recompute failed"); }
    setRecomputing(false);
  };

  const handleRequestAnalyst = async () => {
    try {
      const res = await client.post("/verification/request-review", {
        assessment_id: assessmentId, entity_type: "assessment",
        notes: "Requesting analyst review from upgrade flow."
      });
      toast.success(res.data.message);
      onSuccess?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Request failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="upgrade-flow">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="bg-[#0F172A] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <p className="text-white font-bold text-sm">Upgrade Verification</p>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg"><X size={15} /></button>
        </div>

        <div className="p-6">
          {/* Step 0: Requirements */}
          {step === 0 && (
            <div>
              <h3 className="text-base font-bold text-[#0F172A] mb-1">What's Required</h3>
              <p className="text-sm text-slate-500 mb-5">
                {requirements.length === 0
                  ? "Your data meets the requirements for System Validated status."
                  : "Address the following to qualify for System Validated verification:"}
              </p>
              <div className="space-y-2 mb-5">
                {requirements.length === 0 ? (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                    <CheckCircle2 size={16} /> All requirements met — ready to recompute.
                  </div>
                ) : requirements.map((r, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${r.done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                    <r.icon size={15} className={`mt-0.5 flex-shrink-0 ${r.done ? "text-emerald-600" : "text-slate-400"}`} />
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">{r.label}</p>
                      <p className="text-xs text-slate-500">{r.desc}</p>
                    </div>
                    {r.done ? <Check size={14} className="text-emerald-600 ml-auto mt-0.5 flex-shrink-0" /> :
                      <Lock size={12} className="text-slate-300 ml-auto mt-0.5 flex-shrink-0" />}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                {!allMet && (
                  <button onClick={() => setStep(1)} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50">
                    Improve My Data
                  </button>
                )}
                <button onClick={() => setStep(allMet ? 1 : 1)} className={`${allMet ? "flex-1" : ""} px-4 py-2.5 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 flex items-center justify-center gap-2`}>
                  {allMet ? <><Zap size={14} /> Recompute Now</> : <><ArrowRight size={14} /> Continue</>}
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Improve Data */}
          {step === 1 && (
            <div>
              <h3 className="text-base font-bold text-[#0F172A] mb-1">Improve Your Data</h3>
              <p className="text-sm text-slate-500 mb-5">Complete these steps, then come back to recompute.</p>
              <div className="space-y-2 mb-5">
                {[
                  { icon: FileText, label: "Add activity records", desc: "Open your assessment and add missing data", link: `/assessments/${assessmentId}?tab=activity-data` },
                  { icon: Upload,   label: "Upload evidence documents", desc: "Go to Evidence Vault and attach supporting files", link: "/evidence" },
                  { icon: Shield,   label: "Run input validation", desc: "Check for data quality issues", link: `/assessments/${assessmentId}?tab=intelligence` },
                ].map((item, i) => (
                  <a key={i} href={item.link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-[#059669] transition-colors group">
                    <div className="w-9 h-9 bg-slate-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-50">
                      <item.icon size={16} className="text-slate-400 group-hover:text-[#059669]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#0F172A]">{item.label}</p>
                      <p className="text-xs text-slate-400">{item.desc}</p>
                    </div>
                    <ExternalLink size={12} className="text-slate-300 group-hover:text-[#059669]" />
                  </a>
                ))}
              </div>
              <button onClick={() => { setStep(0); handleRecompute(); }}
                disabled={recomputing}
                data-testid="recompute-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50">
                {recomputing ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
                {recomputing ? "Running..." : "Validate & Recompute"}
              </button>
            </div>
          )}

          {/* Step 2: Result */}
          {step === 2 && result && (
            <div className="text-center">
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 ${result.upgraded ? "bg-emerald-50" : "bg-amber-50"}`}>
                {result.upgraded ? <CheckCircle2 size={28} className="text-[#059669]" /> : <AlertCircle size={28} className="text-amber-500" />}
              </div>
              <h3 className="text-base font-bold text-[#0F172A] mb-2">
                {result.upgraded ? "Verification Upgraded!" : "Validation Complete"}
              </h3>
              <p className="text-sm text-slate-500 mb-4">{result.message}</p>

              {result.upgraded ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                  <p className="text-sm font-semibold text-emerald-700">System Validated</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Confidence score: {fmt(result.overall_score)}/100</p>
                </div>
              ) : (
                <div className="space-y-2 text-left mb-4">
                  {result.issue_count > 0 && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded">{result.issue_count} validation issue(s) remain. Address errors to qualify.</p>}
                  {(result.overall_score||0) < 40 && <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">Score {fmt(result.overall_score)}/100 — minimum 40 required.</p>}
                </div>
              )}

              <div className="flex gap-2">
                {!result.upgraded && (
                  <button onClick={handleRequestAnalyst}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50">
                    Request Analyst Review
                  </button>
                )}
                <button onClick={onClose}
                  className={`${result.upgraded ? "flex-1" : ""} px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-xl hover:bg-slate-800`}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main VerificationCard ─────────────────────────────────────────────────
export default function VerificationCard({ assessmentId, compact = false }) {
  const [data,        setData]       = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [showDetails, setDetails]    = useState(false);
  const [showUpgrade, setUpgrade]    = useState(false);

  const load = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const res = await client.get(`/verification/assessment/${assessmentId}`);
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  if (!assessmentId) return null;
  if (loading) return (
    <div className="h-16 bg-slate-100 rounded-xl animate-pulse" />
  );
  if (!data) return null;

  const lvl = LEVELS[data.verification_level] || LEVELS.self_reported;
  const currentIdx = lvl.idx;

  // Compact mode: just badge + dots + link
  if (compact) {
    return (
      <>
        <button onClick={() => setDetails(true)} data-testid="verif-card-compact"
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity ${lvl.bg} ${lvl.text} ${lvl.border}`}>
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: lvl.color}} />
          {lvl.label}
          <LevelDots currentIdx={currentIdx} />
        </button>
        {showDetails && <DetailsModal data={data} onClose={() => setDetails(false)} onUpgradeClick={() => { setDetails(false); setUpgrade(true); }} />}
        {showUpgrade && <UpgradeFlow data={data} assessmentId={assessmentId} onClose={() => setUpgrade(false)} onSuccess={load} />}
      </>
    );
  }

  // Full card
  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="verification-card">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Verification Status</p>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${lvl.text}`}>{lvl.label}</span>
              <LevelDots currentIdx={currentIdx} />
            </div>
          </div>
          <Shield size={16} style={{color: lvl.color}} />
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-slate-50 rounded-lg p-2.5">
            <p className="text-xs text-slate-400">Confidence</p>
            <p className="text-base font-bold tabular-nums" style={{color: lvl.color}}>
              {data.not_yet_scored ? "—" : `${fmt(data.confidence_score)}%`}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5">
            <p className="text-xs text-slate-400">Evidence</p>
            <p className="text-base font-bold tabular-nums text-[#0F172A]">{fmt(data.evidence_coverage_pct)}%</p>
          </div>
        </div>

        {(data.key_risks||[]).length > 0 && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs mb-3 ${RISK_STYLE[(data.key_risks[0]?.level||"low")]}`}>
            <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">{data.key_risks[0]?.text}</span>
          </div>
        )}

        <p className="text-xs text-slate-400 mb-3">{data.next_step}</p>

        <div className="flex gap-2">
          <button onClick={() => setDetails(true)} data-testid="verif-view-details-btn"
            className="flex-1 px-3 py-2 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50">
            View Details
          </button>
          {data.verification_level !== "third_party_verified" && (
            <button onClick={() => setUpgrade(true)} data-testid="verif-upgrade-btn"
              className="flex-1 px-3 py-2 text-white text-xs font-semibold rounded-lg hover:opacity-90"
              style={{backgroundColor: lvl.color}}>
              Upgrade Verification
            </button>
          )}
        </div>
      </div>

      {showDetails && <DetailsModal data={data} onClose={() => setDetails(false)} onUpgradeClick={() => { setDetails(false); setUpgrade(true); }} />}
      {showUpgrade && <UpgradeFlow data={data} assessmentId={assessmentId} onClose={() => setUpgrade(false)} onSuccess={load} />}
    </>
  );
}
